'use client';

import cs from 'classnames';
import Link from 'next/link';
import { styled } from 'styled-components';
import { ReactNode } from 'react';
import Media from '@dvd/components/Media';
import BaseText from '@dvd/components/BaseText';
import type { CtaProps, ImageProps } from '@types';

type MultiColumnChildProps = {
  /**
   * Optional headline text displayed as an H3 heading below the media.
   */
  headline?: string;

  /**
   * Optional descriptive paragraph text displayed below the headline.
   * - Use a single string for a single paragraph or rich text for multi-paragraph copy.
   */
  description?: ReactNode;

  /**
   * Optional CTA link displayed below the description of the column
   */
  cta?: CtaProps;

  /**
   * The hashed ID of the video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  hashedId?: string;
  /**
   * Aspect ratio of the media to prevent layout shifts and minimize precieved loading time.
   * Grab the aspect ratio from the Wistia app. Find the video in the app, click on it, and
   * then click on the "Embed" button. Then click "Show embed code". Look for the "aspect"
   * attribute. Example: 0.75 for 3:4, 1.7777777777777777 for 16:9, 1 for 1:1.',
   * - **Required** for video content (mutually exclusive with imageSrc)
   */
  aspect?: number;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   */
  image?: ImageProps;
};

const MultiColumnChildMedia = styled(Media)`
  margin-block-end: ${({ theme }) => theme.spacing[2.25]};
`;

const MultiColumnChildContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mq.minWidth.medium} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing[2]};

    &.mediaOnly {
      grid-template-columns: 1fr;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: flex;
    flex-direction: column;
    gap: unset;
  }
`;

/**
 * `MultiColumnChild` component displayed by the `MultiColumn` module.
 *
 * Represents a single column in a MultiColumn layout. Each child can display:
 * - Video content (using hashedId)
 * - Image content (using imageSrc, imageAlt, imageHeight, imageWidth)
 * - Optional headline and description text
 * - Optional CTA link
 *
 * Usage patterns:
 * 1. With Content: Provide headline and/or description for text content below media
 * 2. Media Only: Omit headline and description for media-only display
 *
 * **Note:** All MultiColumnChild components within the same MultiColumn should follow
 * the same pattern (either all with content or all media-only) for consistent layout.
 */
const MultiColumnChild = ({
  headline = null,
  description = null,
  cta = null,
  hashedId,
  aspect,
  image,
}: MultiColumnChildProps) => {
  const mediaContent = hashedId ? (
    <MultiColumnChildMedia hashedId={hashedId} aspect={aspect} />
  ) : (
    <MultiColumnChildMedia
      alt={image.alt}
      height={image.height}
      src={image.src}
      width={image.width}
    />
  );

  return (
    <MultiColumnChildContainer
      className={cs({ mediaOnly: !cta && !headline && !description })}
    >
      {cta ? <Link href={cta.href}>{mediaContent}</Link> : mediaContent}
      {cta ? (
        <BaseText
          body={description}
          ctaLink={cta}
          heading={headline}
          headingLevel="h3"
        />
      ) : null}
      {!cta && (headline || description) ? (
        <BaseText body={description} heading={headline} headingLevel="h3" />
      ) : null}
    </MultiColumnChildContainer>
  );
};

export default MultiColumnChild;
